import { useState, useEffect, useRef } from 'react';
import { Card, Alert, Button, LoadingSpinner } from '@components/ui';
import { useRoleSwitcher } from '@/features/auth/hooks';
import { useSchool } from '@/contexts/SchoolContext';
import schoolNotificationService from '@/features/notifications/services/schoolNotificationService';
import StandardPage from '@/components/loading/StandardPage';

/**
 * Weekly Summary Settings
 * Simple settings page for configuring weekly summaries
 */
const WeeklySummarySettings = ({ embedded = false, context = 'weekly-summary' } = {}) => {
  const { userPermissions, loading: roleLoading } = useRoleSwitcher();
  const { schoolId } = useSchool();
  const hasPermission = userPermissions?.admin;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Track if we've already loaded settings to prevent re-fetch on context updates
  const settingsLoadedRef = useRef(false);
  // Store full school settings for merging on save
  const fullSchoolSettingsRef = useRef(null);

  const [settings, setSettings] = useState({
    weeklySummaryEnabled: false,
    weeklySummaryDay: 'friday',
    weeklySummaryTime: '15:00',
    weeklySummaryChannels: ['telegram', 'email'],
    weeklySummaryIncludeParticipation: true,
  });

  // Load settings only once when schoolId is available
  useEffect(() => {
    const loadSettings = async () => {
      if (!schoolId || settingsLoadedRef.current) return;

      setLoading(true);
      try {
        const schoolSettings = await schoolNotificationService.getSchoolNotificationSettings(schoolId);
        if (schoolSettings) {
          // Store full settings for merging on save
          fullSchoolSettingsRef.current = schoolSettings;
          setSettings({
            weeklySummaryEnabled: schoolSettings.weeklySummaryEnabled ?? false,
            weeklySummaryDay: schoolSettings.weeklySummaryDay ?? 'friday',
            weeklySummaryTime: schoolSettings.weeklySummaryTime ?? '15:00',
            weeklySummaryChannels: schoolSettings.weeklySummaryChannels ?? ['telegram', 'email'],
            weeklySummaryIncludeParticipation: schoolSettings.weeklySummaryIncludeParticipation ?? true,
          });
        }
        settingsLoadedRef.current = true;
      } catch (err) {
        console.error('Failed to load settings:', err);
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [schoolId]);

  const saveSettings = async () => {
    if (!schoolId) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Get current full settings (fetch fresh if not cached)
      let baseSettings = fullSchoolSettingsRef.current;
      if (!baseSettings) {
        baseSettings = await schoolNotificationService.getSchoolNotificationSettings(schoolId);
      }

      // Merge our weekly summary settings with the full school settings
      const mergedSettings = {
        ...baseSettings,
        ...settings,
      };
      await schoolNotificationService.updateSchoolNotificationSettings(schoolId, mergedSettings);
      // Update the ref with the new merged settings
      fullSchoolSettingsRef.current = mergedSettings;
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Combined loading state
  const pageLoading = roleLoading || loading;
  const recognitionContext = context === 'recognition';

  if (embedded && pageLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Permission check
  if (!pageLoading && !hasPermission) {
    return (
      <div className={embedded ? '' : 'p-6'}>
        <Alert variant="error" title="Access Denied">
          You don’t have permission to access these settings.
        </Alert>
      </div>
    );
  }

  const content = (
    <div className={embedded ? 'space-y-5' : 'p-6 max-w-2xl mx-auto'}>
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-base-content mb-2">
            Weekly Summary Settings
          </h1>
          <p className="text-base-content/70">
            Configure automated weekly summaries for parents
          </p>
        </div>
      )}

      {error && (
        <Alert variant="error" className="mb-6" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-6" onDismiss={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card className={embedded
        ? "[&_.card-body]:p-4 sm:[&_.card-body]:p-6"
        : "p-6"}>
        <h2 className="text-xl font-semibold mb-4">
          {recognitionContext ? 'Weekly parent summary' : 'Weekly Summary'}
        </h2>
        <p className="text-sm text-base-content/70 mb-6">
          {recognitionContext ? (
            <>This uses Swan’s established weekly parent-summary schedule. A learner with no recorded incidents receives one Clean Week recognition, and their linked parent receives the existing summary.</>
          ) : (
            <>When enabled, parents receive a weekly summary of their child’s behavior. Students with no incidents receive a “Clean Week” recognition. Students with incidents receive a summary message.</>
          )}
        </p>

        {/* Enable toggle */}
        <div className="flex flex-col gap-1.5 mb-6">
          <label className="label cursor-pointer justify-start gap-4">
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.weeklySummaryEnabled}
              onChange={(e) => updateSetting('weeklySummaryEnabled', e.target.checked)}
            />
            <span className="label-text font-medium">Enable Weekly Summaries</span>
          </label>
        </div>

        {settings.weeklySummaryEnabled && (
          <div className="space-y-4 pl-4 border-l-2 border-primary/20">
            {/* Day selection */}
            <div className="flex flex-col gap-1.5">
              <label className="label">
                <span className="label-text">Day of Week</span>
              </label>
              <select
                className="select select-bordered w-full max-w-xs"
                value={settings.weeklySummaryDay}
                onChange={(e) => updateSetting('weeklySummaryDay', e.target.value)}
              >
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
            </div>

            {/* Time selection */}
            <div className="flex flex-col gap-1.5">
              <label className="label">
                <span className="label-text">Time</span>
              </label>
              <input
                type="time"
                className="input input-bordered w-full max-w-xs"
                value={settings.weeklySummaryTime}
                onChange={(e) => updateSetting('weeklySummaryTime', e.target.value)}
              />
              <label className="label">
                <span className="label-text-alt">South Africa time (SAST)</span>
              </label>
            </div>

            {/* Channels */}
            <div className="flex flex-col gap-1.5">
              <label className="label">
                <span className="label-text">Notification Channels</span>
              </label>
              <div className="flex flex-wrap gap-4">
                {['telegram', 'email'].map(channel => (
                  <label key={channel} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-primary"
                      checked={settings.weeklySummaryChannels.includes(channel)}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setSettings(prev => ({
                          ...prev,
                          weeklySummaryChannels: isChecked
                            ? [...prev.weeklySummaryChannels, channel]
                            : prev.weeklySummaryChannels.filter(c => c !== channel)
                        }));
                      }}
                    />
                    <span className="capitalize">{channel}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Sections to include */}
            <div className="flex flex-col gap-1.5">
              <label className="label">
                <span className="label-text">Sections to include</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary"
                  checked={settings.weeklySummaryIncludeParticipation}
                  onChange={(e) => updateSetting('weeklySummaryIncludeParticipation', e.target.checked)}
                />
                <span>Participation (yes / no / passed-on tallies)</span>
              </label>
            </div>
          </div>
        )}

        {/* Save button */}
        <div className="mt-6 pt-4 border-t">
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </Card>

      {/* Info card */}
      <Card className={embedded
        ? "mt-6 bg-info/10 [&_.card-body]:p-4 sm:[&_.card-body]:p-6"
        : "p-6 mt-6 bg-info/10"}>
        <h3 className="font-semibold mb-2">How it works</h3>
        <ul className="list-disc list-inside space-y-1 text-sm text-base-content/80">
          <li><strong>Clean Week:</strong> Students with no incidents receive a “Good Behaviour” recognition</li>
          <li><strong>Incidents:</strong> Parents of students with incidents receive a summary message</li>
          <li>Clean Week recognitions trigger the standard recognition alert to parents</li>
          <li>The system checks each hour and sends at your configured day/time</li>
        </ul>
      </Card>
    </div>
  );

  if (embedded) return content;

  return (
    <StandardPage loading={pageLoading} skeleton={<LoadingSpinner size="lg" />}>
      {content}
    </StandardPage>
  );
};

export default WeeklySummarySettings;
